import { useEffect, useState } from 'react'
import { createAudioAnalyser, type RemoteAudioTrack } from 'livekit-client'
import { AudioLines, VolumeOff } from 'lucide-react'
import { IconButton, Popover, Slider } from '@/components/play'
import type { SourceSound } from '@/lib/watch/useSourceAudio'
import { useViewerVolume } from '@/lib/watch/useViewerVolume'
import type { WatchCamera } from '@/lib/watch/types'
import { SourceGlyph } from './SourceGlyph'
import './watch.css'

const PERCENT = { style: 'unit', unit: 'percent' } as const

export interface SourceVolumeProps {
  /** What the host's audience follows (`useWatchScreen`'s `audienceSource`): the Live show or a camera. */
  source: WatchCamera
  /** Its audio as the room lists it (`useSourceAudio`). */
  sound: SourceSound
  /** Its track while this tab receives it, for the level line; null otherwise. */
  track: RemoteAudioTrack | null
  /** How loud it plays for the audience, 0–100 (`useSourceVolume`). */
  level: number
  /** The slider moved: heard on this tab at once. */
  onPreview(level: number): void
  /** The drag or key press ended: sent to the stage. */
  onCommit(level: number): void
}

/** What the popover says of the source when it isn't simply sounding, so a silent slider never looks broken. */
function stateWords(source: WatchCamera, sound: SourceSound): string | null {
  if (!source.online) return `${source.name} is offline`
  if (sound === 'none') return `No sound from ${source.name}`
  if (sound === 'muted') return `${source.name} is muted`
  return null
}

/**
 * A live host's level for the source under their voice (docs/features/stage-volume.md): the dock's button, named with
 * the source and its level, opens a popover with the source's name, its slider and a line that moves with its sound. The
 * level is the stage's: the audience hears the source at it under the faces, each at their own volume. Offline, muted
 * or silent, the popover says so; with no sound published the slider waits, disabled. At 0% the button shows the
 * speaker struck through.
 */
export function SourceVolume({ source, sound, track, level, onPreview, onCommit }: SourceVolumeProps) {
  const [open, setOpen] = useState(false)
  // The line reads the source as this tab plays it, and a browser reads nothing from a track it doesn't play (at 0, or
  // with the host's own speaker muted), so it shows only while this tab plays the source.
  const { muted } = useViewerVolume()
  const audible = level > 0 && !muted
  const state = stateWords(source, sound)
  const name = `${source.name} volume for your audience, ${level}%`
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      side="top"
      title={
        <span className="watch-source-volume__title">
          <SourceGlyph role={source.sourceRole} />
          {source.name}
        </span>
      }
      label={`${source.name} volume`}
      className="watch-source-volume"
      trigger={
        <IconButton variant="ghost" label={name}>
          {level === 0 ? <VolumeOff /> : <AudioLines />}
        </IconButton>
      }
    >
      <Slider
        label="Volume for your audience"
        step={5}
        format={PERCENT}
        value={level}
        disabled={source.online && sound === 'none'}
        onValueChange={onPreview}
        onValueCommitted={onCommit}
      />
      {state ? (
        <p className="watch-source-volume__state">{state}</p>
      ) : (
        open && audible && track && <SourceLevel track={track} />
      )}
    </Popover>
  )
}

/**
 * The source's sound as it arrives, a thin line under the slider while the popover is open: it lies flat when the source
 * is silent, so the host can tell a quiet source from a low slider. Decorative: the words above say the state.
 */
function SourceLevel({ track }: { track: RemoteAudioTrack }) {
  const [level, setLevel] = useState(0)
  useEffect(() => {
    let analyser: ReturnType<typeof createAudioAnalyser>
    try {
      analyser = createAudioAnalyser(track)
    } catch {
      return
    }
    let frame = 0
    let last = 0
    const tick = (now: number) => {
      // Ten times a second is enough to see sound.
      if (now - last >= 100) {
        last = now
        setLevel(Math.min(1, analyser.calculateVolume()))
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(frame)
      void analyser.cleanup()
    }
  }, [track])
  return (
    <span className="watch-source-volume__level" aria-hidden="true">
      <span style={{ transform: `scaleX(${level})` }} />
    </span>
  )
}
